import { asyncHandler } from '../async-handler';
import store from '../../../store/store';
import { RequestInfo } from '../../../interfaces/RequestInfo';
import { CREATE_SHOP_API } from '../../constants/central-api';
import { applicationWWWXRLEncodedBodyBuilder } from '../../utils/body-builder';
import { createShopAsEmployee } from '../identity/identity-api';
import isNotEmptyString from '../../helper-functions/isNotEmptyString';

export interface CreateShopResponse {
  success: boolean;
  data?: {
    slug: string;
    id: number;
    name: string;
  };
  message?: string;
  isLimitReached?: boolean;
  // The organisation already has a brand with this name: central-api created
  // nothing and answered with that existing brand instead (it is idempotent on
  // name so a retried onboarding never mints a duplicate). Callers adding a
  // NEW brand must ask for a different name rather than switch to `data`.
  alreadyExists?: boolean;
}

export async function createShop(
  name: string,
  webshopUrl?: string
): Promise<CreateShopResponse> {
  const state = store.getState();
  const accessToken = state.auth.authorization.accessToken;
  const isWhiteLabel = state.UI.isWhiteLabel;
  const seller_id = state.user.profile.seller_id;
  const userRole = state.user.profile.role;

  // Hotfix 2026-08-06: invite-only employee sessions (agency emp_ bearer,
  // no seller JWT) cannot reach the seller-side create endpoint — route them
  // to the employee-authenticated POST /agency/shops instead. Seller and
  // white-label sessions keep the legacy path below unchanged.
  const agencyToken = state.auth.agency?.accessToken;
  if (
    !isWhiteLabel &&
    !isNotEmptyString(accessToken?.sellerToken) &&
    isNotEmptyString(agencyToken)
  ) {
    try {
      const res = await createShopAsEmployee(name, webshopUrl);
      if (res?.success === true && res.data) {
        return {
          success: true,
          data: res.data,
          alreadyExists: res.already_exists === true,
        };
      }
      return {
        success: false,
        message: res?.message || 'Shop creation failed',
      };
    } catch (err: any) {
      const message = String(err?.message ?? 'Shop creation failed');
      const lower = message.toLowerCase();
      return {
        success: false,
        message,
        isLimitReached:
          err?.status === 422 &&
          (lower.includes('maximum') || lower.includes('limit')),
      };
    }
  }

  const body: { name: string; currency: string; role?: string } = {
    name,
    currency: 'EUR',
  };
  if (userRole === 'seller' || userRole === 'agency') {
    body.role = userRole;
  }

  const requestInfo: RequestInfo = {
    url: CREATE_SHOP_API(seller_id),
    method: 'POST',
    body: applicationWWWXRLEncodedBodyBuilder(body),
    headers: {
      Accept: 'application/json',
      'Content-type': 'application/x-www-form-urlencoded',
      Authorization: `${accessToken.type} ${
        isWhiteLabel
          ? accessToken.currentWhiteLabelSellerToken
          : accessToken.sellerToken
      }`,
    },
  };

  const actionType = 'CREATE_SHOP';
  const customMsg = 'Company Added Successfully.';

  const res: any = await asyncHandler(
    requestInfo,
    actionType,
    customMsg,
    false,
    false,
    true
  );

  const response = res?.data;

  if (response?.success === true) {
    return {
      success: true,
      data: response.data,
      // 201 = created; 200 = an existing brand with this name was returned.
      alreadyExists: res?.responseStatus === 200,
    };
  } else {
    // Check if the error is related to brand limit
    const errorMessage =
      response?.message ||
      (Array.isArray(response?.errors)
        ? response.errors.join(' ')
        : typeof response?.errors === 'object'
        ? Object.values(response.errors).join(' ')
        : '') ||
      '';
    const errorMessageLower = errorMessage.toLowerCase();
    const errorCode = (
      response?.error ||
      response?.error_code ||
      ''
    ).toLowerCase();

    const isLimitReached =
      errorMessageLower.includes('limit') ||
      errorMessageLower.includes('maximum') ||
      errorMessageLower.includes('exceeded') ||
      errorMessageLower.includes('brand limit') ||
      errorMessageLower.includes('shop limit') ||
      errorCode === 'brand_limit_reached' ||
      errorCode === 'shop_limit_reached' ||
      errorCode.includes('limit');

    return {
      success: false,
      message: errorMessage,
      isLimitReached,
    };
  }
}
